import { Text } from "@smooth/design-system-components"
import { TerminalBadge } from "../components/parts"
import { isExpected, isUnexpected, isUnsetItem } from "./comparison"
import { JUDGMENT_LABELS } from "./types"
import type { BatchItem } from "./types"

type Props = {
  items: BatchItem[]
  /** ケースの詳細から戻ってきたときに印を付けるケース */
  selectedCaseId: number | null
  onSelect: (item: BatchItem) => void
  emptyMessage: string
}

/** 結果の一覧。1行は「ケース名 ｜ 期待 [R] → 結果 [R] ｜ ✓／✗」。行を押すとケースの詳細を開く */
export function ResultList({ items, selectedCaseId, onSelect, emptyMessage }: Props) {
  if (items.length === 0) return <Text>{emptyMessage}</Text>
  return (
    <ul aria-label="結果の一覧" className="flex flex-col border-t border-border-default">
      {items.map((item) => (
        <li key={item.id}>
          <ResultRow item={item} selected={item.caseId === selectedCaseId} onSelect={onSelect} />
        </li>
      ))}
    </ul>
  )
}

function ResultRow({
  item,
  selected,
  onSelect,
}: {
  item: BatchItem
  selected: boolean
  onSelect: (item: BatchItem) => void
}) {
  const expected = isExpected(item)
  const unexpected = isUnexpected(item)
  const unset = isUnsetItem(item)
  return (
    <button
      type="button"
      aria-current={selected || undefined}
      onClick={() => onSelect(item)}
      className={[
        "grid w-full grid-cols-[minmax(0,1fr)_auto_2rem] items-center gap-x-4 border-b border-border-default px-2.5 py-2 text-left text-body text-text-default",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring",
        selected ? "bg-bg-info" : "hover:bg-bg-muted",
      ].join(" ")}
    >
      <span className="min-w-0 [overflow-wrap:anywhere]">{item.caseTitle}</span>
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-caption">
        <TerminalBadge id={item.expectedTerminal} />→
        {item.judgment === "failed" ? (
          JUDGMENT_LABELS.failed
        ) : item.observedTerminal ? (
          <TerminalBadge id={item.observedTerminal} mismatch={unexpected} />
        ) : (
          "—"
        )}
      </span>
      <span
        aria-label={
          unset ? "期待は未定" : expected ? "期待どおり" : unexpected ? "期待と違う" : "生成中"
        }
        className={`text-center font-bold ${unexpected ? "text-text-error" : "text-text-default"}`}
      >
        {unset ? "—" : expected ? "✓" : unexpected ? "✗" : ""}
      </span>
    </button>
  )
}
